import { Injectable } from '@angular/core';
import { AppStateService } from '@services/app-state.service';
import { ProfileService } from '@services/profile.service';
import { SignInService } from '@services/sign-in.service';
import { SiteConfigService } from '@services/site-config.service';
import { StorageService } from '@services/storage.service';
import { FavoritesConfig, FundShareClassId } from '@types';
import { BehaviorSubject, Observable, of, Subject, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';

/**
 * Favorites Service to support different components and different implementations for different sites.
 *
 * TODO May need to make methods product type aware.
 * TODO implement external storage service when available.
 */
@Injectable({
  providedIn: 'root',
})
export class FavoritesService {
  private favoritesConfig: FavoritesConfig;
  private favorites: FundShareClassId[] = [];
  private favorites$: BehaviorSubject<FundShareClassId[]> = new BehaviorSubject(
    []
  );

  constructor(
    appStateService: AppStateService,
    private siteConfig: SiteConfigService,
    private storageService: StorageService,
    private profileService: ProfileService,
    private signInService: SignInService
  ) {
    this.favoritesConfig = appStateService.getFavoritesConfig();
    if (this.favoritesConfig.hasFavorites) {
      this.hasFavorites$().subscribe((hasFavorites) => {
        if (hasFavorites) {
          this.loadFavorites();
        } else {
          this.favorites = [];
          this.favorites$.next(this.favorites);
        }
      });
    }
  }

  isFavouriteEnabled(): boolean {
    return (
      this.favoritesConfig.hasFavorites &&
      this.siteConfig.common?.enableFavoriteFunds
    );
  }

  getFavoriteConfig(): FavoritesConfig {
    return this.favoritesConfig;
  }

  hasFavorites$(): Observable<boolean> {
    if (this.isLoggedInOnly()) {
      return this.profileService.isLoggedIn$();
    }
    return of(this.isFavouriteEnabled());
  }

  getFavorites$(): Observable<FundShareClassId[]> {
    return this.favorites$.asObservable();
  }

  isFavorite$(fund: FundShareClassId) {
    return this.getFavorites$().pipe(
      map((favorites) => favorites.indexOf(fund) !== -1)
    );
  }

  hideSignModalAfterLogin$(): Observable<boolean> {
    return combineLatest([
      this.signInService.setSignInModal$(),
      this.profileService.isLoggedIn$(),
    ]).pipe(
      map((latestLogin) => {
        const [isSignInModalSet, isProfileLoggedIn] = latestLogin;
        return isSignInModalSet && isProfileLoggedIn;
      })
    );
  }

  toggleFavorite(fund: FundShareClassId): void {
    const index = this.favorites.indexOf(fund);
    if (index !== -1) {
      this.favorites.splice(index, 1);
    } else {
      this.favorites.push(fund);
    }
    this.favorites$.next(this.favorites);
    this.saveFavorites();
  }

  private isLoggedInOnly(): boolean {
    return this.isFavouriteEnabled() && this.favoritesConfig.loggedInOnly;
  }

  private saveFavorites() {
    if (this.favoritesConfig.useLocalStorage) {
      this.storageService.storeFundFavorites(this.favorites);
    }
  }

  private loadFavorites() {
    if (this.favoritesConfig.useLocalStorage) {
      this.storageService.retrieveFundFavorites().then((favorites) => {
        if (favorites) {
          this.favorites = favorites;
        } else {
          this.favorites = [];
        }
        this.favorites$.next(this.favorites);
      });
    }
  }
}
